// src/scenes/contracts/ContractFormPage/index.jsx
import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';

import { tokens } from '../../../theme';
import { greenColor } from '../../../config/colors';
import { useTranslation } from '../../../hooks/useTranslation';
import { parseBR, formatNumericInput } from '../../../utils';
import api from '../../../services/api';
import ValoresTab from './ValoresTab';
import ClienteTab from './ClienteTab';
import RevendaTab from './RevendaTab';
import GarantiasTab from './GarantiasTab';

// Estado inicial vazio do formulário
const buildInitialFormData = () => ({
  // Aba Valores
  valorTotal: '',
  valorFinanciado: '',
  valorEntrada: '',
  parcelas: '',
  valorParcela: '',
  jurosMensal: '',
  totalJuros: '',
  iof: '',
  tac: '',
  cet: '',
  operacional: '',
  indice: 'CDI',
  baseCalculo: 'DIAS_UTEIS_365',
  tabelaCalculo: 'PRICE',

  // Aba Cliente
  clienteNome: '',
  clienteEmail: '',
  clienteTelefone: '',
  clienteCpfCnpj: '',
  razaoSocial: '',
  inscricaoMunicipal: '',
  inscricaoEstadual: '',
  regimeTributario: '',
  emailResponsavel: '',
  telefoneResponsavel: '',
  enderecoRua: '',
  cep: '',
  estado: '',
  cidade: '',
  socios: [{ id: Date.now(), nome: '', email: '', telefone: '', cpf: '', rg: '' }],
  // Documentos obrigatórios Grafeno
  docComprovanteEndereco: null,
  docFaturamento12meses: null,
  docContratoSocial: null,

  // Aba Revenda
  repassePorcentagem: '',
  repasseValor: '',
  revendaNome: '',
  revendaCnpj: '',
  revendaInscMunicipal: '',
  revendaInscEstadual: '',
  revendaRegimeTributario: '',
  revendaLogradouro: '',
  revendaNumero: '',
  revendaBairro: '',
  revendaComplemento: '',
  revendaCep: '',
  revendaEstado: '',
  revendaCidade: '',
  revendaNomeFinanceiro: '',
  revendaEmailFinanceiro: '',
  revendaEmailCredito: '',
  revendaSocios: [{ id: Date.now(), nome: '', email: '', cpf: '', rg: '' }],

  // Aba Garantias
  garantias: [{ id: Date.now(), value: '' }],
});

// Validação dos campos obrigatórios antes do submit
const validate = (formData, isEditing) => {
  const errors = {};
  if (!String(formData.valorTotal || '').trim()) errors.valorTotal = true;
  if (!String(formData.valorFinanciado || '').trim()) errors.valorFinanciado = true;
  if (!String(formData.parcelas || '').trim()) errors.parcelas = true;
  if (!formData.clienteNome) errors.clienteNome = true;
  if (!formData.clienteEmail) errors.clienteEmail = true;
  if (!formData.clienteCpfCnpj) errors.clienteCpfCnpj = true;
  if (!formData.razaoSocial) errors.razaoSocial = true;
  // Documentos obrigatórios Grafeno — apenas em criação, não em edição
  if (!isEditing) {
    if (!formData.docComprovanteEndereco) errors.docComprovanteEndereco = true;
    if (!formData.docFaturamento12meses) errors.docFaturamento12meses = true;
    if (!formData.docContratoSocial) errors.docContratoSocial = true;
  }
  return errors;
};

// Define em qual aba focar quando há erros
const getFirstErrorTab = (errors) => {
  const valoresFields = ['valorTotal', 'valorFinanciado', 'parcelas'];
  const clienteFields = [
    'clienteNome', 'clienteEmail', 'clienteCpfCnpj', 'razaoSocial',
    'docComprovanteEndereco', 'docFaturamento12meses', 'docContratoSocial',
  ];
  if (valoresFields.some((f) => errors[f])) return 0;
  if (clienteFields.some((f) => errors[f])) return 1;
  return 0;
};

// Converte _formPayload salvo (strings com máscaras BR) de volta para o formData
// Os campos de moeda/percentual já estão no formato display "250.000,00" — usamos diretamente
const mapPayloadToFormData = (fp) => {
  if (!fp) return null;
  return {
    ...buildInitialFormData(),
    ...fp,
    // Garantir arrays nunca vazios
    socios: fp.socios?.length ? fp.socios : [{ id: Date.now(), nome: '', email: '', telefone: '', cpf: '', rg: '' }],
    revendaSocios: fp.revendaSocios?.length ? fp.revendaSocios : [{ id: Date.now(), nome: '', email: '', cpf: '', rg: '' }],
    garantias: fp.garantias?.length ? fp.garantias : [{ id: Date.now(), value: '' }],
    // Documentos não são reenviados na edição
    docComprovanteEndereco: null,
    docFaturamento12meses: null,
    docContratoSocial: null,
  };
};

const ContractFormPage = ({ isEditing = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  // tokens é importado mas usado via theme.palette para manter consistência com o projeto
  tokens(theme.palette.mode);

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState(buildInitialFormData);
  const [options, setOptions] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [errors, setErrors] = useState({});

  const s = 'contractForm';

  // Buscar opções para os selects (credores, garantias, índices, etc.)
  useEffect(() => {
    api.get('/v1/contracts/options')
      .then(({ data }) => setOptions(data.data))
      .catch((err) => console.error('[contractForm/options]', err));
  }, []);

  // Carregar contrato para edição
  useEffect(() => {
    if (!isEditing || !id) return;

    setLoadingEdit(true);
    api.get(`/v1/contracts/${id}`)
      .then(({ data }) => {
        const contract = data.data;
        const fp = contract._formPayload;
        if (fp) {
          const mapped = mapPayloadToFormData(fp);
          setFormData(mapped);
        } else {
          // Fallback: pré-preencher apenas o que estiver disponível na listagem
          setFormData((prev) => ({
            ...prev,
            clienteNome: contract.client || '',
            valorTotal: contract.value
              ? formatNumericInput(String(contract.value), { type: 'currency', mode: 'display' })
              : '',
          }));
        }
      })
      .catch((err) => {
        console.error('[contractForm/load]', err);
        toast.error(t(`${s}.errorLoad`));
      })
      .finally(() => setLoadingEdit(false));
  }, [isEditing, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((fields) => {
    setFormData((prev) => ({ ...prev, ...fields }));
    // Limpar erros dos campos que foram alterados
    const erroredFields = Object.keys(fields).filter((k) => errors[k]);
    if (erroredFields.length > 0) {
      setErrors((prev) => {
        const next = { ...prev };
        erroredFields.forEach((k) => delete next[k]);
        return next;
      });
    }
  }, [errors]);

  const handleSave = async () => {
    const validationErrors = validate(formData, isEditing);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setActiveTab(getFirstErrorTab(validationErrors));
      toast.error(t(`${s}.validation.required`));
      return;
    }

    const payload = {
      simulation_id: null,
      client_name: formData.clienteNome,
      // Valores — converter para número usando parseBR (formato "250.000,00" → 250000)
      valorTotal: formData.valorTotal,
      valorFinanciado: formData.valorFinanciado,
      valorEntrada: formData.valorEntrada,
      parcelas: formData.parcelas,
      valorParcela: formData.valorParcela,
      jurosMensal: formData.jurosMensal,
      totalJuros: formData.totalJuros,
      iof: formData.iof,
      tac: formData.tac,
      cet: formData.cet,
      operacional: formData.operacional,
      indice: formData.indice,
      baseCalculo: formData.baseCalculo,
      tabelaCalculo: formData.tabelaCalculo,
      // Cliente
      clienteEmail: formData.clienteEmail,
      clienteTelefone: formData.clienteTelefone,
      clienteCpfCnpj: formData.clienteCpfCnpj,
      razaoSocial: formData.razaoSocial,
      inscricaoMunicipal: formData.inscricaoMunicipal,
      inscricaoEstadual: formData.inscricaoEstadual,
      regimeTributario: formData.regimeTributario,
      emailResponsavel: formData.emailResponsavel,
      telefoneResponsavel: formData.telefoneResponsavel,
      enderecoRua: formData.enderecoRua,
      cep: formData.cep,
      estado: formData.estado,
      cidade: formData.cidade,
      socios: formData.socios,
      // Documentos obrigatórios → custom_fields Grafeno
      custom_fields: {
        doc_comprovante_endereco: formData.docComprovanteEndereco?.name ?? null,
        doc_faturamento_12meses: formData.docFaturamento12meses?.name ?? null,
        doc_contrato_social: formData.docContratoSocial?.name ?? null,
      },
      // Revenda
      repassePorcentagem: formData.repassePorcentagem,
      repasseValor: formData.repasseValor,
      revendaNome: formData.revendaNome,
      revendaCnpj: formData.revendaCnpj,
      revendaInscMunicipal: formData.revendaInscMunicipal,
      revendaInscEstadual: formData.revendaInscEstadual,
      revendaRegimeTributario: formData.revendaRegimeTributario,
      revendaLogradouro: formData.revendaLogradouro,
      revendaNumero: formData.revendaNumero,
      revendaBairro: formData.revendaBairro,
      revendaComplemento: formData.revendaComplemento,
      revendaCep: formData.revendaCep,
      revendaEstado: formData.revendaEstado,
      revendaCidade: formData.revendaCidade,
      revendaNomeFinanceiro: formData.revendaNomeFinanceiro,
      revendaEmailFinanceiro: formData.revendaEmailFinanceiro,
      revendaEmailCredito: formData.revendaEmailCredito,
      revendaSocios: formData.revendaSocios,
      // Garantias
      garantias: formData.garantias,
      // Numeric values parsed from BR format for backend use
      _numeric: {
        valorTotal: parseBR(formData.valorTotal) || 0,
        valorFinanciado: parseBR(formData.valorFinanciado) || 0,
        valorEntrada: parseBR(formData.valorEntrada) || 0,
        valorParcela: parseBR(formData.valorParcela) || 0,
        jurosMensal: parseBR(formData.jurosMensal) || 0,
        totalJuros: parseBR(formData.totalJuros) || 0,
        iof: parseBR(formData.iof) || 0,
        tac: parseBR(formData.tac) || 0,
        cet: parseBR(formData.cet) || 0,
        operacional: parseBR(formData.operacional) || 0,
        repassePorcentagem: parseBR(formData.repassePorcentagem) || 0,
        repasseValor: parseBR(formData.repasseValor) || 0,
      },
    };

    try {
      setSubmitting(true);
      if (isEditing && id) {
        await api.put(`/v1/contracts/${id}`, payload);
      } else {
        await api.post('/v1/contracts', payload);
      }
      toast.success(t(`${s}.success`));
      navigate('/app/contratos');
    } catch (err) {
      console.error('[contractForm/save]', err);
      toast.error(t(`${s}.error`));
    } finally {
      setSubmitting(false);
    }
  };

  const tabProps = { formData, onChange: handleChange, options, errors };

  const TABS = [
    t(`${s}.tabs.valores`),
    t(`${s}.tabs.cliente`),
    t(`${s}.tabs.revenda`),
    t(`${s}.tabs.garantias`),
  ];

  return (
    <Box sx={{ m: '20px' }}>
      {/* Link "Voltar para contratos" */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 1,
          cursor: 'pointer',
          width: 'fit-content',
          '&:hover': { opacity: 0.75 },
        }}
        onClick={() => navigate('/app/contratos')}
      >
        <ArrowBackIcon sx={{ fontSize: 16, color: '#555' }} />
        <Typography sx={{ fontSize: '13px', color: '#555' }}>
          {t(`${s}.backButton`)}
        </Typography>
      </Box>

      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '28px' }}>
            {isEditing ? t(`${s}.titleEdit`) : t(`${s}.title`)}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#9e9e9e', mt: 0.5 }}>
            {t(`${s}.subtitle`)}
          </Typography>
        </Box>
        {isEditing && id && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: '11px', color: '#9e9e9e' }}>ID do Contrato</Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>#{id}</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Card com abas e conteúdo */}
      <Box sx={{ backgroundColor: '#fff', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        {/* Abas */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            px: 2,
            borderBottom: '1px solid #e0e0e0',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              color: '#9e9e9e',
              minWidth: 80,
            },
            '& .Mui-selected': { color: '#333 !important' },
            '& .MuiTabs-indicator': { backgroundColor: greenColor, height: 3 },
          }}
        >
          {TABS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>

        {/* Conteúdo da aba */}
        <Box sx={{ p: 3 }}>
          {loadingEdit ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} sx={{ color: greenColor }} />
            </Box>
          ) : (
            <>
              {activeTab === 0 && <ValoresTab {...tabProps} />}
              {activeTab === 1 && <ClienteTab {...tabProps} />}
              {activeTab === 2 && <RevendaTab formData={formData} onChange={handleChange} />}
              {activeTab === 3 && (
                <GarantiasTab formData={formData} onChange={handleChange} options={options} />
              )}
            </>
          )}
        </Box>

        {/* Rodapé sticky */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            px: 3,
            py: 2,
            borderTop: '1px solid #e0e0e0',
            position: 'sticky',
            bottom: 0,
            backgroundColor: '#fff',
            zIndex: 10,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate('/app/contratos')}
            sx={{
              borderColor: '#e0e0e0',
              color: '#555',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: '#bdbdbd', backgroundColor: '#f5f5f5' },
            }}
          >
            {t(`${s}.cancelButton`)}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={submitting || loadingEdit}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              bgcolor: greenColor,
              color: '#000',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: '#6ec13b' },
              '&.Mui-disabled': { bgcolor: '#c8e6c9', color: '#555' },
            }}
          >
            {submitting ? t(`${s}.savingButton`) : t(`${s}.saveButton`)}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ContractFormPage;
